import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachHeart from "./wrapper/EachHeart";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class Heart extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleDrawHeart = this.handleDrawHeart.bind(this);
    this.handleEndDrawHeart = this.handleEndDrawHeart.bind(this);
    this.handleDragHeart = this.handleDragHeart.bind(this);
    this.handleDragHeartComplete = this.handleDragHeartComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("hearts").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragHeart(index, newValues) {
    this.setState({
      override: {
        index,
        ...newValues,
      },
    });
  }

  handleDragHeartComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { hearts } = this.props;
      const newHearts = hearts.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              x: override.x,
              y: override.y,
              sx: override.sx !== undefined ? override.sx : each.sx,
              sy: override.sy !== undefined ? override.sy : each.sy,
              angle: override.angle !== undefined ? override.angle : each.angle,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newHearts, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState(
        {
          current: {
            ...current,
            x2: xyValue[0],
            y2: xyValue[1],
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    } else {
      this.setState(
        {
          current: {
            x: xyValue[0],
            y: xyValue[1],
            x2: null,
            y2: null,
            sx: 1,
            sy: 1,
            angle: 0,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDrawHeart(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleEndDrawHeart(xyValue, moreProps, e) {
    const { hearts, appearance } = this.props;
    const { current } = this.state;

    if (!isDefined(current) || !isDefined(current.x)) {
      return;
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    // Calculate scale based on drag distance
    const x2 = xyValue[0];
    const y2 = xyValue[1];

    // Default scale if just clicked (no drag)
    let sx = 1;
    let sy = 1;
    let angle = 0;

    // If dragged, calculate scale from pixel distance
    if (current.x2 !== null && current.y2 !== null) {
      const centerX = xScale(current.x);
      const centerY = yScale(current.y);
      const x2Pixel = xScale(x2);
      const y2Pixel = yScale(y2);

      const dx = x2Pixel - centerX;
      const dy = y2Pixel - centerY;

      // CRITICAL: Use same formula as resize handle - baseDim = width, divide by (baseDim / 2)
      const distance = Math.sqrt(dx * dx + dy * dy);
      const baseDim = 23; // dimension[0] - must match helper method
      if (distance > 5) {
        sx = Math.max(0.2, distance / (baseDim / 2));
        sy = sx; // Keep proportional initially
      }
    }

    const newHearts = [
      ...hearts.map((d) => ({ ...d, selected: false })),
      {
        x: current.x,
        y: current.y,
        // Don't store x2/y2 - they're only for initial drawing
        sx,
        sy,
        angle,
        selected: true,
        appearance,
      },
    ];

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newHearts, moreProps, e);
      },
    );
  }

  render() {
    const { appearance, hearts, hoverText } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionStrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { current, override } = this.state;

    const tempHeart =
      isDefined(current) && isDefined(current.x) ? (
        <EachHeart
          interactive={false}
          x={current.x}
          y={current.y}
          x2={current.x2}
          y2={current.y2}
          sx={current.sx}
          sy={current.sy}
          angle={current.angle}
          stroke={
            appearance.stroke && typeof appearance.stroke === "object"
              ? appearance.stroke
              : {
                  color: appearance.stroke || "#FF4081",
                  width: 2,
                  opacity: 1,
                  dash: "Solid",
                }
          }
          fill={
            appearance.fill && typeof appearance.fill === "object"
              ? appearance.fill
              : { color: appearance.fill || "#FF4081", opacity: 0.6 }
          }
        />
      ) : null;

    return (
      <g>
        {hearts.map((each, idx) => {
          const eachAppearance = {
            stroke: each.appearance?.stroke ||
              appearance.stroke || {
                color: "#FF4081",
                width: 2,
                opacity: 1,
                dash: "Solid",
              },
            fill: each.appearance?.fill ||
              appearance.fill || { color: "#FF4081", opacity: 0.6 },
          };

          return (
            <EachHeart
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...(idx === override?.index ? override : each)}
              stroke={eachAppearance.stroke}
              fill={eachAppearance.fill}
              onDrag={this.handleDragHeart}
              onDragComplete={this.handleDragHeartComplete}
            />
          );
        })}
        {tempHeart}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionStrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEndDrawHeart}
          onMouseMove={this.handleDrawHeart}
          data-evo-id="interactive-heart"
        />
      </g>
    );
  }
}

Heart.propTypes = {
  enabled: PropTypes.bool.isRequired,

  snap: PropTypes.bool.isRequired,
  shouldDisableSnap: PropTypes.func.isRequired,
  snapTo: PropTypes.func,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionStrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    fill: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
  }).isRequired,

  hearts: PropTypes.array.isRequired,

  hoverText: PropTypes.object.isRequired,
};

Heart.defaultProps = {
  snap: false,
  shouldDisableSnap: () => true,
  snapTo: null,

  onStart: noop,
  onComplete: noop,

  currentPositionStroke: "#FF4081",
  currentPositionOpacity: 1,
  currentPositionStrokeWidth: 2,
  currentPositionRadius: 4,

  hearts: [],

  hoverText: {
    enable: false,
    selectedText: "",
    text: "Clique e arraste para criar um coração",
    bgHeight: "auto",
    bgWidth: "auto",
  },
};

export default Heart;
